import React, { useCallback, useState } from 'react'
import I18n from 'APP/I18n'
import useScreenView from 'APP/Hooks/useScreenView'

import { usePlacesSearch } from 'APP/Hooks/PlacesSearch'
import Button from 'APP/Converse/Button'
import { AutocompleteInput, AutocompleteOption } from '../Inputs/AutocompleteInput'
import Icon from 'APP/Converse/Icon'
import { loadPlaceOptions } from 'APP/Lib/PlacesAutocompleteHelpers'
import { AddressSearchContainer, AddressButtonContainer } from './style'
import { ContentContainer } from '../SearchCard/style'
import { StyleProp, ViewStyle } from 'react-native'
import { useSelector } from 'APP/Hooks/redux'
import { selectPatientProfileLocationCoordinates } from 'APP/Redux/PatientRedux'
import { PlaceDetails } from '@dialogue/coredata'

export interface AddressPickerSearchCardProps {
  placeholder?: string
  type: string
  onSubmitCallback?: (place: PlaceDetails | undefined, option?: AutocompleteOption | null) => void
  style?: StyleProp<ViewStyle>
  readyToOpen?: boolean
}

const AddressPickerSearchCard = ({
  placeholder,
  type,
  onSubmitCallback,
  style,
  readyToOpen = true,
}: AddressPickerSearchCardProps) => {
  const [, , resolvePlace] = usePlacesSearch({
    type: type,
  })
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>('')
  const [option, setOption] = useState<AutocompleteOption | null>(null)
  const coordinates = useSelector(selectPatientProfileLocationCoordinates)
  const { isSmallScreen } = useScreenView()

  const getOnAddressSubmit = (placeID: string) => {
    resolvePlace(placeID).then((resolvedPlace) => {
      onSubmitCallback?.(resolvedPlace, option)
    })
  }

  const loadOptions = useCallback(
    async (input: string, { signal }: { signal: AbortSignal }): Promise<AutocompleteOption[]> => {
      return await loadPlaceOptions({
        input,
        placeType: type,
        signal,
        origin: coordinates,
      })
    },
    [type, coordinates]
  )

  const handleChange = useCallback((option: AutocompleteOption | null) => {
    if (option == null) {
      setOption(null)
      setSelectedPlaceId('')
      return
    }

    setOption(option)
    setSelectedPlaceId(option.value)
  }, [])

  return (
    <ContentContainer style={style}>
      <AddressSearchContainer>
        <AutocompleteInput
          icon={<Icon src="search" size={20} color="accent" variant="materialIcons" />}
          placeholder={placeholder}
          option={option}
          onChange={handleChange}
          loadOptions={loadOptions}
          debounceTime={300}
          maxLength={100}
          testID="ADDRESS_PICKER"
          readyToOpen={readyToOpen}
        />
      </AddressSearchContainer>
      <AddressButtonContainer>
        <Button
          testID="SUBMIT_BUTTON"
          variant="primary"
          title={I18n.t('AddressSearchCard.confirm')}
          onPress={() => getOnAddressSubmit(selectedPlaceId)}
          disabled={!selectedPlaceId}
          widthVariant={isSmallScreen ? 'full' : 'fixed'}
        />
      </AddressButtonContainer>
    </ContentContainer>
  )
}

export default AddressPickerSearchCard
